import { useParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import ProductsTabSearchAndFilter from "./search-and-filter";
import GridViewProductCard from "./cards/grid-view-product-card";
import ListViewProductCard from "./cards/list-view-product-card";
import UpcomingEvents from "@/modules/events/components/upcoming-events";
import { Loader2Icon, ArrowRightIcon } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { TGetListingProductsResponseDTO } from "@/app/api/listing/[slug]/products/listing-products.dto";
import DemoListViewCard from "@/app/(main)/categories/fractional-investment-v2/components/demo-list-view-card";
import { TGetCategoryDataResponseDTO } from "@/types/dto/category.dto";

const ProductsFeed = ({
	productsData,
	filterRegions,
}: {
	productsData?: TGetListingProductsResponseDTO;
	filterRegions?: TGetCategoryDataResponseDTO["productFilterRegions"];
}) => {
	const { slug } = useParams() as { slug: string };

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize] = useState(10);
	const [products, setProducts] = useState(productsData || []);
	const [isLoading, setIsLoading] = useState(false);

	const [showGridView, setShowGridView] = useState<boolean>(false);
	const showEventsSection = slug === "esg-reporting" || slug === "smart-water-metering-monitoring";

	useEffect(() => {
		if (productsData) {
			setProducts(productsData);
		}
	}, [productsData]);

	useEffect(() => {
		if (!productsData || productsData.length === 0) {
			setIsLoading(true);
			fetch(`/api/products`)
				.then((res) => res.json())
				.then((data) => setProducts(data?.data || []))
				.catch((error) => console.error("Error fetching fallback products:", error))
				.finally(() => setIsLoading(false));
		}
	}, [productsData, slug]);

	const paginatedProducts = useMemo(() => {
		const sorted = [...products].sort((a, b) => {
			if (a.isFeatured && !b.isFeatured) return -1;
			if (!a.isFeatured && b.isFeatured) return 1;
			return 0;
		});

		const startIndex = (currentPage - 1) * pageSize;
		return sorted.slice(startIndex, startIndex + pageSize);
	}, [products, currentPage, pageSize]);

	const totalPages = Math.ceil(products.length / pageSize);

	return (
		<div>
			<div className="relative min-h-[600px]">
				<ProductsTabSearchAndFilter
					filterRegions={filterRegions}
					initialResults={productsData || []}
					setResults={setProducts}
					showGridView={showGridView}
					setShowGridView={setShowGridView}
				/>

				{isLoading ? (
					<div className="flex justify-center py-10">
						<Loader2Icon className="animate-spin" />
					</div>
				) : (
					<div
						className={cn([
							"my-4",
							showGridView
								? "grid grid-cols-2 gap-4 sm:gap-8 md:grid-cols-4"
								: "flex flex-col gap-8",
						])}
					>
						{paginatedProducts.map((product) =>
							product.isFeatured && !showGridView ? (
								<DemoListViewCard
									key={product.id}
									productId={product.id}
									features={product.features ?? []}
									tags={product.tags ?? []}
									{...product}
									isPremium={true}
								/>
							) : showGridView ? (
								<GridViewProductCard key={product.id} {...product} />
							) : (
								<ListViewProductCard key={product.id} {...product} />
							)
						)}
					</div>
				)}
			</div>

			<div className="mt-8">
				<div className="flex items-center justify-between">
					<button
						disabled={currentPage <= 1}
						onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
						className="px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
					>
						Previous
					</button>
					<span className="text-sm text-gray-500">
						Page {currentPage} of {totalPages}
					</span>
					<button
						disabled={currentPage >= totalPages}
						onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
						className="px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
					>
						Next
					</button>
				</div>
			</div>

			{showEventsSection && (
				<section className="mt-10 sm:mt-16">
					<div className="mb-7 flex items-center justify-between gap-x-4">
						<h2 className="whitespace-nowrap text-xl font-bold text-gray-900 md:text-2xl">
							Prominent Webinars
						</h2>
						<div className="h-[0.5px] w-full bg-gray-200" />
						<Link
							href="/events"
							className="flex w-fit items-center justify-start gap-x-2 text-sm font-medium text-brand-primary"
						>
							<span className="whitespace-nowrap">See more events</span>
							<ArrowRightIcon className="h-4 w-4" />
						</Link>
					</div>
					<div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
						<UpcomingEvents />
					</div>
				</section>
			)}
		</div>
	);
};

export default ProductsFeed;
